// Smart Daara - Physical school: grades entry (teacher spreadsheet-style)
import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { physicalAPI, gradesAPI } from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Plus, Save, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const STAFF_ROLES = ['school_admin', 'manager', 'censeur'];

export default function GradesEntry() {
  const { user } = useAuth();
  const { language } = useLanguage();
  const fr = language === 'fr';
  const isStaff = STAFF_ROLES.includes(user?.role);
  const [assignments, setAssignments] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [students, setStudents] = useState([]);
  const [evaluations, setEvaluations] = useState([]);
  const [sel, setSel] = useState({ class_id: '', subject_id: '', period_id: '' });
  const [cells, setCells] = useState({}); // {evalId: {studentId: value}}
  const [apps, setApps] = useState({}); // {studentId: text}
  const [averages, setAverages] = useState({});
  const [evalDialog, setEvalDialog] = useState(false);
  const [evalForm, setEvalForm] = useState({ title: '', eval_type: 'devoir', date: '', max_scale: 20, weight: 1 });
  const [classStatus, setClassStatus] = useState('draft');

  useEffect(() => {
    (async () => {
      const a = isStaff ? (await physicalAPI.listAssignments()).data : (await physicalAPI.myAssignments()).data;
      setAssignments(a);
      const years = (await physicalAPI.listYears()).data;
      const active = years.find((y) => y.is_active);
      if (active) {
        const p = (await physicalAPI.listPeriods(active.id)).data;
        setPeriods(p);
        const open = p.find((x) => x.status === 'open') || p[0];
        if (open && a.length) setSel({ class_id: a[0].class_id, subject_id: a[0].subject_id, period_id: open.id });
      }
    })().catch(() => {});
  }, []);

  const pairs = [];
  const seen = new Set();
  assignments.forEach((a) => {
    const key = `${a.class_id}|${a.subject_id}`;
    if (!seen.has(key)) { seen.add(key); pairs.push(a); }
  });

  const loadGrid = async () => {
    if (!sel.class_id || !sel.subject_id || !sel.period_id) return;
    const [st, ev] = await Promise.all([
      physicalAPI.listStudents(sel.class_id),
      gradesAPI.listEvaluations({ class_id: sel.class_id, subject_id: sel.subject_id, period_id: sel.period_id }),
    ]);
    setStudents(st.data);
    setEvaluations(ev.data);
    const cellMap = {};
    for (const e of ev.data) {
      const gr = (await gradesAPI.gradesByEvaluation(e.id)).data;
      cellMap[e.id] = {};
      gr.forEach((g) => {
        cellMap[e.id][g.student_user_id] = g.status !== 'present' ? g.status : (g.score ?? '');
      });
    }
    setCells(cellMap);
    try {
      const summ = (await gradesAPI.classSummary(sel.class_id, sel.period_id)).data;
      const av = {}; const ap = {};
      summ.students.forEach((s) => {
        const row = s.subjects.find((r) => r.subject_id === sel.subject_id);
        av[s.student_user_id] = row?.avg;
        ap[s.student_user_id] = row?.appreciation || '';
      });
      setAverages(av); setApps(ap);
      setClassStatus(summ.students.some((s) => s.status === 'published') ? 'published' : (summ.students[0]?.status || 'draft'));
    } catch (e) { /* teacher without summary access */ }
  };
  useEffect(() => { loadGrid().catch(() => {}); }, [sel]);

  const createEval = async () => {
    if (!evalForm.title) { toast.error(fr ? 'Titre requis' : 'Title required'); return; }
    try {
      await gradesAPI.createEvaluation({ ...evalForm, class_id: sel.class_id, subject_id: sel.subject_id, period_id: sel.period_id });
      toast.success(fr ? 'Évaluation créée' : 'Evaluation created');
      setEvalDialog(false);
      setEvalForm({ title: '', eval_type: 'devoir', date: '', max_scale: 20, weight: 1 });
      loadGrid();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const saveAll = async () => {
    try {
      for (const ev of evaluations) {
        const grades = students.map((st) => {
          const raw = String(cells[ev.id]?.[st.user_id] ?? '').trim().toUpperCase();
          if (raw === '') return null;
          if (raw === 'ABS' || raw === 'DISP') return { student_user_id: st.user_id, score: null, status: raw };
          const num = parseFloat(raw.replace(',', '.'));
          if (isNaN(num)) return null;
          return { student_user_id: st.user_id, score: num, status: 'present' };
        }).filter(Boolean);
        if (grades.length) await gradesAPI.bulkGrades({ evaluation_id: ev.id, grades });
      }
      for (const st of students) {
        if (apps[st.user_id] !== undefined && String(apps[st.user_id]).trim() !== '') {
          await gradesAPI.setAppreciation({ class_id: sel.class_id, subject_id: sel.subject_id,
            period_id: sel.period_id, student_user_id: st.user_id, text: apps[st.user_id] });
        }
      }
      toast.success(fr ? 'Notes enregistrées' : 'Grades saved');
      loadGrid();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const selPair = pairs.find((p) => p.class_id === sel.class_id && p.subject_id === sel.subject_id);
  const locked = classStatus === 'published' && !isStaff;

  return (
    <div className="space-y-5" data-testid="grades-entry-page">
      {locked && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 text-sm rounded-lg px-4 py-2" data-testid="published-banner">
          {fr ? '🔒 Bulletins publiés : les notes sont verrouillées. Toute modification passe par l\'administration.'
              : '🔒 Report cards published: grades are locked. Contact the administration for changes.'}
        </div>
      )}
      {classStatus === 'published' && isStaff && (
        <div className="bg-sky-50 border border-sky-200 text-sky-800 text-sm rounded-lg px-4 py-2" data-testid="audited-banner">
          {fr ? 'ℹ️ Bulletins publiés : vos modifications sont autorisées et historisées (journal d\'audit).'
              : 'ℹ️ Report cards published: your changes are allowed and recorded in the audit log.'}
        </div>
      )}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-outfit font-bold text-slate-900">{fr ? 'Saisie des notes' : 'Grades entry'}</h1>
        <div className="flex gap-2">
          <Button variant="outline" disabled={!sel.class_id || locked} onClick={() => setEvalDialog(true)} data-testid="new-eval-btn">
            <Plus className="w-4 h-4 mr-2" />{fr ? 'Nouvelle évaluation' : 'New evaluation'}
          </Button>
          <Button className="bg-emerald-700" disabled={!evaluations.length || locked} onClick={saveAll} data-testid="save-grades-btn">
            <Save className="w-4 h-4 mr-2" />{fr ? 'Enregistrer' : 'Save'}
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap gap-3">
        <div>
          <Label className="text-xs">{fr ? 'Classe · Matière' : 'Class · Subject'}</Label>
          <select className="h-10 border border-slate-200 rounded-md px-2 text-sm block min-w-[220px]"
            value={selPair ? `${sel.class_id}|${sel.subject_id}` : ''}
            onChange={(e) => { const [c, s] = e.target.value.split('|'); setSel({ ...sel, class_id: c, subject_id: s }); }}
            data-testid="grades-pair-select">
            <option value="">—</option>
            {pairs.map((a) => (
              <option key={a.id} value={`${a.class_id}|${a.subject_id}`}>{a.class_name} · {a.subject_name}</option>
            ))}
          </select>
        </div>
        <div>
          <Label className="text-xs">{fr ? 'Période' : 'Period'}</Label>
          <select className="h-10 border border-slate-200 rounded-md px-2 text-sm block" value={sel.period_id}
            onChange={(e) => setSel({ ...sel, period_id: e.target.value })} data-testid="grades-period-select">
            {periods.map((p) => <option key={p.id} value={p.id}>{p.name}{p.status === 'closed' ? (fr ? ' (clôturée)' : ' (closed)') : ''}</option>)}
          </select>
        </div>
        <p className="text-xs text-slate-400 self-end pb-2">{fr ? '💡 Tapez une note, ou ABS / DISP' : '💡 Type a score, or ABS / DISP'}</p>
      </div>

      {sel.class_id && (
        <Card className="border-0 shadow-sm overflow-x-auto">
          <CardContent className="p-0">
            <table className="w-full text-sm min-w-[700px]">
              <thead>
                <tr className="bg-slate-50 text-left">
                  <th className="p-3 text-slate-500">{fr ? 'Élève' : 'Student'}</th>
                  {evaluations.map((ev) => (
                    <th key={ev.id} className="p-2 text-slate-600">
                      <div className="flex items-center gap-1">
                        <span>{ev.title}<br /><span className="text-[10px] font-normal text-slate-400">/{ev.max_scale} · {fr ? 'poids' : 'w'} {ev.weight}</span></span>
                        {!locked && (
                        <button className="text-red-300 hover:text-red-500" data-testid={`delete-eval-${ev.id}`} onClick={async () => {
                          if (!window.confirm(fr ? `Supprimer "${ev.title}" et toutes ses notes ?` : `Delete "${ev.title}" and all its grades?`)) return;
                          try { await gradesAPI.deleteEvaluation(ev.id); loadGrid(); }
                          catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
                        }}><Trash2 className="w-3 h-3" /></button>
                        )}
                      </div>
                    </th>
                  ))}
                  <th className="p-2 text-emerald-700">{fr ? 'Moy.' : 'Avg'}</th>
                  <th className="p-2 text-slate-600">{fr ? 'Appréciation' : 'Remark'}</th>
                </tr>
              </thead>
              <tbody>
                {students.map((st) => (
                  <tr key={st.user_id} className="border-t border-slate-100">
                    <td className="p-3 font-medium whitespace-nowrap">{st.first_name} {st.last_name}</td>
                    {evaluations.map((ev) => (
                      <td key={ev.id} className="p-1">
                        <Input className="w-20 h-8 text-center" disabled={locked}
                          value={cells[ev.id]?.[st.user_id] ?? ''}
                          onChange={(e) => setCells({ ...cells, [ev.id]: { ...cells[ev.id], [st.user_id]: e.target.value } })}
                          data-testid={`grade-cell-${ev.id}-${st.user_id}`} />
                      </td>
                    ))}
                    <td className="p-2 font-bold text-emerald-700">{averages[st.user_id] ?? '—'}</td>
                    <td className="p-1">
                      <Input className="h-8 min-w-[160px]" value={apps[st.user_id] ?? ''} disabled={locked}
                        onChange={(e) => setApps({ ...apps, [st.user_id]: e.target.value })}
                        placeholder={fr ? 'Appréciation…' : 'Remark…'} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {evaluations.length === 0 && <p className="text-slate-400 text-sm text-center py-6">{fr ? 'Aucune évaluation — créez-en une' : 'No evaluations — create one'}</p>}
          </CardContent>
        </Card>
      )}

      <Dialog open={evalDialog} onOpenChange={setEvalDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{fr ? 'Nouvelle évaluation' : 'New evaluation'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>{fr ? 'Titre' : 'Title'}</Label><Input value={evalForm.title} onChange={(e) => setEvalForm({ ...evalForm, title: e.target.value })} data-testid="eval-title-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Type</Label>
                <select className="w-full h-10 border border-slate-200 rounded-md px-2 text-sm" value={evalForm.eval_type} onChange={(e) => setEvalForm({ ...evalForm, eval_type: e.target.value })}>
                  <option value="devoir">{fr ? 'Devoir' : 'Assignment'}</option>
                  <option value="composition">{fr ? 'Composition' : 'Exam'}</option>
                  <option value="interrogation">{fr ? 'Interrogation' : 'Quiz'}</option>
                  <option value="oral">Oral</option>
                </select>
              </div>
              <div><Label>Date</Label><Input type="date" value={evalForm.date} onChange={(e) => setEvalForm({ ...evalForm, date: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>{fr ? 'Noté sur' : 'Scale'}</Label><Input type="number" value={evalForm.max_scale} onChange={(e) => setEvalForm({ ...evalForm, max_scale: parseFloat(e.target.value) || 20 })} /></div>
              <div><Label>{fr ? 'Poids' : 'Weight'}</Label><Input type="number" step="0.5" value={evalForm.weight} onChange={(e) => setEvalForm({ ...evalForm, weight: parseFloat(e.target.value) || 1 })} /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEvalDialog(false)}>{fr ? 'Annuler' : 'Cancel'}</Button>
            <Button className="bg-emerald-700" onClick={createEval} data-testid="eval-create-btn">{fr ? 'Créer' : 'Create'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
